import { create } from "zustand";
import type { GeneratedWorkout, WorkoutBlock } from "@/types";
import { workoutService, getNextWeekStart } from "@/services/workoutService";
import { getWeekStart } from "@/lib/workouts/mockWorkouts";
import { useAuthStore } from "@/state/authStore";
import { useProfileStore, flushPendingProfileSave } from "@/state/profileStore";
import { toast } from "@/components/Toast/useToast";
import { nid } from "@/lib/workouts/workoutUtils";
import { captureOperationalError } from "@/lib/monitoring/sentry";

/** Helper — get userId without using the React hook (safe outside components). */
function getUserId(): string | null {
  return useAuthStore.getState().account?.id ?? null;
}

const SYNC_MAP_KEY = "wahoo-sync-map";
const SYNC_HASH_KEY = "wahoo-sync-hashes";
const STALE_KEY = "wahoo-stale";

function loadWahooStaleIds(): Set<string> {
  try {
    const s = localStorage.getItem(STALE_KEY);
    if (!s) return new Set();
    return new Set((JSON.parse(s) as string[]).map(nid));
  } catch { return new Set(); }
}

function persistStaleIds(ids: Set<string>): void {
  try { localStorage.setItem(STALE_KEY, JSON.stringify([...ids])); } catch { /* ignore */ }
}


function loadWahooSyncMap(): Record<string, string> {
  try {
    const s = localStorage.getItem(SYNC_MAP_KEY);
    if (!s) return {};
    return JSON.parse(s) as Record<string, string>;
  } catch {
    return {};
  }
}

function loadWahooSyncHashes(): Record<string, string> {
  try {
    const s = localStorage.getItem(SYNC_HASH_KEY);
    if (!s) return {};
    return JSON.parse(s) as Record<string, string>;
  } catch {
    return {};
  }
}

function persistSyncHashes(hashes: Record<string, string>): void {
  try {
    localStorage.setItem(SYNC_HASH_KEY, JSON.stringify(hashes));
  } catch {
    /* ignore */
  }
}

/** Debounce timer for persisting sync data to Supabase profile. */
let _syncPersistTimer: ReturnType<typeof setTimeout> | null = null;
const SYNC_PERSIST_DEBOUNCE_MS = 2000;

/** Persist wahooSyncMap and wahooSyncHashes to profiles.data via profileStore. */
function persistSyncDataToProfile(
  syncMap: Record<string, string>,
  syncHashes: Record<string, string>,
): void {
  if (_syncPersistTimer !== null) clearTimeout(_syncPersistTimer);
  _syncPersistTimer = setTimeout(() => {
    _syncPersistTimer = null;
    useProfileStore.getState().updateProfile({
      wahooSyncMap: syncMap,
      wahooSyncHashes: syncHashes,
    });
  }, SYNC_PERSIST_DEBOUNCE_MS);
}

function onSaveError(label: string) {
  return (err: unknown) => {
    const detail =
      err instanceof Error
        ? err.message
        : typeof err === "string"
          ? err
          : "Unknown error";
    console.error(`[workoutStore] ${label} save failed:`, err);
    captureOperationalError(err, "workout.save", { label });
    toast.error("Workout changes weren't saved", {
      message: detail,
    });
  };
}

type WorkoutStore = {
  workouts: GeneratedWorkout[];
  isWeekLocked: boolean;
  /** True once the initial load from DB has completed (lock state is settled). */
  workoutsLoaded: boolean;
  setWeekLocked: (locked: boolean) => void;
  setWorkouts: (workouts: GeneratedWorkout[]) => void;
  addWorkout: (workout: GeneratedWorkout) => void;
  removeWorkout: (workoutId: string) => void;
  updateWorkoutBlocks: (workoutId: string, blocks: WorkoutBlock[]) => void;
  updateWorkoutName: (workoutId: string, name: string) => void;
  getWorkout: (workoutId: string) => GeneratedWorkout | undefined;
  clearWorkouts: () => void;
  /** Load workouts and lock state from Supabase into the store. */
  loadFromDB: (userId: string) => Promise<void>;

  // ── Next-week planning (Sunday after 6 PM) ────────────────────────────────
  /** Workouts planned for the following week. null = not yet planned. */
  nextWeekWorkouts: GeneratedWorkout[] | null;
  isNextWeekLocked: boolean;
  /** Save next-week workouts to state and DB. */
  setNextWeekWorkouts: (workouts: GeneratedWorkout[], userId: string) => void;
  /** Load next-week workouts from DB into the store. */
  loadNextWeekFromDB: (userId: string) => Promise<void>;
  setNextWeekLocked: (locked: boolean, userId: string) => void;

  // ── Historical weeks (read-only, loaded on demand) ────────────────────────
  /** Past weeks keyed by ISO weekStartDate. Populated on demand when user navigates backward. */
  historicalWorkouts: Record<string, GeneratedWorkout[]>;
  loadHistoricalWeek: (userId: string, weekStartDate: string) => Promise<void>;

  // ── Wahoo device sync ─────────────────────────────────────────────────────
  /** workoutId → Wahoo planned-workout ID, set after a successful Wahoo push */
  wahooSyncMap: Record<string, string>;
  setWahooSync: (workoutId: string, wahooId: string) => void;
  clearWahooSync: (workoutId: string) => void;
  clearAllWahooSync: () => void;
  /** Hydrate sync map and hashes from profile data (called once after profile loads). */
  hydrateSyncFromProfile: () => void;
  /** Flush sync data to Supabase immediately, bypassing debounce timers. */
  flushSyncToProfile: () => void;

  /**
   * workoutId → JSON fingerprint of the content pushed to Wahoo at last sync
   * (name + blocksResolved). If the current fingerprint differs, the workout
   * needs re-syncing. Replaces the old wahooNeedsSync dirty-flag approach —
   * staleness is derived automatically, no imperative marking required.
   */
  wahooSyncHashes: Record<string, string>;
  setWahooSyncHash: (workoutId: string, hash: string) => void;

  /** workoutIds explicitly changed by the user (add/edit) since last sync */
  wahooNeedsSync: Set<string>;
  markWahooNeedsSync: (workoutId: string) => void;
  clearWahooNeedsSync: (workoutId: string) => void;

  /** workoutId set when it has been downloaded as a Garmin FIT file */
  garminDownloaded: Set<string>;
  markGarminDownloaded: (workoutId: string) => void;
  unmarkGarminDownloaded: (workoutId: string) => void;
  clearAllGarminDownloaded: () => void;

  /** workoutId set when workout was rebuilt after a Garmin FIT was already downloaded */
  garminNeedsResync: Set<string>;
  markGarminStale: (workoutId: string) => void;
  clearGarminStale: (workoutId: string) => void;
};

export const useWorkoutStore = create<WorkoutStore>()((set, get) => ({
  workouts: [],
  isWeekLocked: false,
  workoutsLoaded: false,
  nextWeekWorkouts: null,
  isNextWeekLocked: false,
  historicalWorkouts: {},
  wahooSyncMap: loadWahooSyncMap(),
  wahooSyncHashes: loadWahooSyncHashes(),
  wahooNeedsSync: loadWahooStaleIds(),
  garminDownloaded: new Set<string>(),
  garminNeedsResync: new Set<string>(),

  setWeekLocked: (locked) => {
    set({ isWeekLocked: locked });
    const uid = getUserId();
    if (uid)
      void workoutService
        .save(uid, getWeekStart(), {
          workouts: get().workouts,
          isWeekLocked: locked,
        })
        .catch(onSaveError("setWeekLocked"));
  },

  setWorkouts: (workouts) => {
    set({ workouts });
    const uid = getUserId();
    if (uid)
      void workoutService
        .save(uid, getWeekStart(), {
          workouts,
          isWeekLocked: get().isWeekLocked,
        })
        .catch(onSaveError("setWorkouts"));
  },

  addWorkout: (workout) => {
    set((state) => {
      const wahooNeedsSync = new Set([...state.wahooNeedsSync, nid(workout.id)]);
      persistStaleIds(wahooNeedsSync);
      return { workouts: [...state.workouts.filter((w) => w.id !== workout.id), workout], wahooNeedsSync };
    });
    const uid = getUserId();
    if (uid)
      void workoutService
        .save(uid, getWeekStart(), {
          workouts: get().workouts,
          isWeekLocked: get().isWeekLocked,
        })
        .catch(onSaveError("addWorkout"));
  },

  removeWorkout: (workoutId) => {
    set((state) => ({
      workouts: state.workouts.filter((w) => w.id !== workoutId),
    }));
    const uid = getUserId();
    if (uid)
      void workoutService
        .save(uid, getWeekStart(), {
          workouts: get().workouts,
          isWeekLocked: get().isWeekLocked,
        })
        .catch(onSaveError("removeWorkout"));
  },

  updateWorkoutBlocks: (workoutId, blocks) => {
    const isNextWeek = !!get().nextWeekWorkouts?.some(
      (w) => w.id === workoutId,
    );
    set((state) => {
      const wahooNeedsSync = new Set([...state.wahooNeedsSync, nid(workoutId)]);
      persistStaleIds(wahooNeedsSync);
      if (isNextWeek) {
        return {
          nextWeekWorkouts: (state.nextWeekWorkouts ?? []).map((w) =>
            w.id === workoutId ? { ...w, blocksResolved: blocks } : w,
          ),
          wahooNeedsSync,
        };
      }
      return {
        workouts: state.workouts.map((w) =>
          w.id === workoutId ? { ...w, blocksResolved: blocks } : w,
        ),
        wahooNeedsSync,
      };
    });
    const uid = getUserId();
    if (uid) {
      if (isNextWeek) {
        void workoutService
          .save(uid, getNextWeekStart(), {
            workouts: get().nextWeekWorkouts ?? [],
            isWeekLocked: get().isNextWeekLocked,
          })
          .catch(onSaveError("updateWorkoutBlocks (next week)"));
      } else {
        void workoutService
          .save(uid, getWeekStart(), {
            workouts: get().workouts,
            isWeekLocked: get().isWeekLocked,
          })
          .catch(onSaveError("updateWorkoutBlocks"));
      }
    }
  },

  updateWorkoutName: (workoutId, name) => {
    const isNextWeek = !!get().nextWeekWorkouts?.some(
      (w) => w.id === workoutId,
    );
    set((state) => {
      const wahooNeedsSync = new Set([...state.wahooNeedsSync, nid(workoutId)]);
      persistStaleIds(wahooNeedsSync);
      if (isNextWeek) {
        return {
          nextWeekWorkouts: (state.nextWeekWorkouts ?? []).map((w) =>
            w.id === workoutId ? { ...w, workoutName: name } : w,
          ),
          wahooNeedsSync,
        };
      }
      return {
        workouts: state.workouts.map((w) =>
          w.id === workoutId ? { ...w, workoutName: name } : w,
        ),
        wahooNeedsSync,
      };
    });
    const uid = getUserId();
    if (uid) {
      if (isNextWeek) {
        void workoutService
          .save(uid, getNextWeekStart(), {
            workouts: get().nextWeekWorkouts ?? [],
            isWeekLocked: get().isNextWeekLocked,
          })
          .catch(onSaveError("updateWorkoutName (next week)"));
      } else {
        void workoutService
          .save(uid, getWeekStart(), {
            workouts: get().workouts,
            isWeekLocked: get().isWeekLocked,
          })
          .catch(onSaveError("updateWorkoutName"));
      }
    }
  },

  getWorkout: (workoutId) =>
    get().workouts.find((w) => w.id === workoutId) ??
    get().nextWeekWorkouts?.find((w) => w.id === workoutId) ??
    Object.values(get().historicalWorkouts)
      .flat()
      .find((w) => w.id === workoutId),

  clearWorkouts: () => set({ workouts: [], isWeekLocked: false, workoutsLoaded: false }),

  loadFromDB: async (userId) => {
    const payload = await workoutService.load(userId, getWeekStart());
    if (payload) {
      set({ workouts: payload.workouts, isWeekLocked: payload.isWeekLocked, workoutsLoaded: true });
    } else {
      set({ workoutsLoaded: true });
    }
  },

  // ── Next-week actions ──────────────────────────────────────────────────────
  setNextWeekWorkouts: (workouts, userId) => {
    set({ nextWeekWorkouts: workouts });
    void workoutService
      .save(userId, getNextWeekStart(), { workouts, isWeekLocked: false })
      .catch(onSaveError("setNextWeekWorkouts"));
  },

  loadNextWeekFromDB: async (userId) => {
    const payload = await workoutService.load(userId, getNextWeekStart());
    if (payload) {
      set({
        nextWeekWorkouts: payload.workouts,
        isNextWeekLocked: payload.isWeekLocked,
      });
    }
  },

  setNextWeekLocked: (locked, userId) => {
    set({ isNextWeekLocked: locked });
    const workouts = get().nextWeekWorkouts ?? [];
    void workoutService
      .save(userId, getNextWeekStart(), { workouts, isWeekLocked: locked })
      .catch(onSaveError("setNextWeekLocked"));
  },

  // ── Historical weeks ──────────────────────────────────────────────────────
  loadHistoricalWeek: async (userId, weekStartDate) => {
    const payload = await workoutService.load(userId, weekStartDate);
    if (payload) {
      set((state) => ({
        historicalWorkouts: {
          ...state.historicalWorkouts,
          [weekStartDate]: payload.workouts,
        },
      }));
    }
  },

  // ── Wahoo sync map ────────────────────────────────────────────────────────
  setWahooSync: (workoutId, wahooId) =>
    set((s) => {
      const next = { ...s.wahooSyncMap, [workoutId]: wahooId };
      try {
        localStorage.setItem(SYNC_MAP_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      persistSyncDataToProfile(next, s.wahooSyncHashes);
      return { wahooSyncMap: next };
    }),

  clearWahooSync: (workoutId) =>
    set((s) => {
      const nextMap = { ...s.wahooSyncMap };
      delete nextMap[workoutId];
      try {
        localStorage.setItem(SYNC_MAP_KEY, JSON.stringify(nextMap));
      } catch {
        /* ignore */
      }
      const nextHashes = { ...s.wahooSyncHashes };
      delete nextHashes[workoutId];
      persistSyncHashes(nextHashes);
      persistSyncDataToProfile(nextMap, nextHashes);
      return { wahooSyncMap: nextMap, wahooSyncHashes: nextHashes };
    }),

  clearAllWahooSync: () => {
    try {
      localStorage.removeItem(SYNC_MAP_KEY);
      localStorage.removeItem(SYNC_HASH_KEY);
      // Clear legacy keys from old approach
      localStorage.removeItem("wahoo-stale");
      localStorage.removeItem("wahoo-sync-scores");
    } catch {
      /* ignore */
    }
    persistSyncDataToProfile({}, {});
    set({ wahooSyncMap: {}, wahooSyncHashes: {}, wahooNeedsSync: new Set() });
  },

  hydrateSyncFromProfile: () => {
    const profile = useProfileStore.getState().profile;
    if (!profile) return;
    const profileMap = profile.wahooSyncMap ?? {};
    const profileHashes = profile.wahooSyncHashes ?? {};
    // Merge profile data with localStorage — profile is authoritative for cross-device
    const localMap = loadWahooSyncMap();
    const localHashes = loadWahooSyncHashes();
    const mergedMap = { ...localMap, ...profileMap };
    const mergedHashes = { ...localHashes, ...profileHashes };
    // Update localStorage with merged data
    try {
      localStorage.setItem(SYNC_MAP_KEY, JSON.stringify(mergedMap));
    } catch { /* ignore */ }
    persistSyncHashes(mergedHashes);
    set({ wahooSyncMap: mergedMap, wahooSyncHashes: mergedHashes });
  },

  flushSyncToProfile: () => {
    // Cancel the pending debounced persist so it doesn't fire after this flush
    if (_syncPersistTimer !== null) {
      clearTimeout(_syncPersistTimer);
      _syncPersistTimer = null;
    }
    // Merge sync data into profile state and save to Supabase immediately
    const { wahooSyncMap, wahooSyncHashes } = get();
    useProfileStore.getState().updateProfile({ wahooSyncMap, wahooSyncHashes });
    flushPendingProfileSave();
  },

  markWahooNeedsSync: (workoutId) =>
    set((s) => { const n = new Set([...s.wahooNeedsSync, nid(workoutId)]); persistStaleIds(n); return { wahooNeedsSync: n }; }),

  clearWahooNeedsSync: (workoutId) =>
    set((s) => { const n = new Set(s.wahooNeedsSync); n.delete(nid(workoutId)); persistStaleIds(n); return { wahooNeedsSync: n }; }),

  // ── Wahoo sync hashes ─────────────────────────────────────────────────────
  setWahooSyncHash: (workoutId, hash) =>
    set((s) => {
      const next = { ...s.wahooSyncHashes, [workoutId]: hash };
      persistSyncHashes(next);
      persistSyncDataToProfile(s.wahooSyncMap, next);
      return { wahooSyncHashes: next };
    }),

  // ── Garmin ────────────────────────────────────────────────────────────────
  markGarminDownloaded: (workoutId) =>
    set((s) => ({
      garminDownloaded: new Set([...s.garminDownloaded, workoutId]),
      garminNeedsResync: (() => {
        const n = new Set(s.garminNeedsResync);
        n.delete(workoutId);
        return n;
      })(),
    })),

  unmarkGarminDownloaded: (workoutId) =>
    set((s) => {
      const next = new Set(s.garminDownloaded);
      next.delete(workoutId);
      return { garminDownloaded: next };
    }),

  clearAllGarminDownloaded: () =>
    set({
      garminDownloaded: new Set<string>(),
      garminNeedsResync: new Set<string>(),
    }),

  markGarminStale: (workoutId) =>
    set((s) => ({
      garminNeedsResync: new Set([...s.garminNeedsResync, workoutId]),
    })),

  clearGarminStale: (workoutId) =>
    set((s) => {
      const next = new Set(s.garminNeedsResync);
      next.delete(workoutId);
      return { garminNeedsResync: next };
    }),
}));
